import * as React from "react";
import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Typography, Dialog, Card, CardBody, CardFooter, Spinner } from "@material-tailwind/react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";

import { Couriers } from "../../../Common/DTOs/Delivery/CourierShipmentResponseModel";
import { orderController } from "../Controllers/OrderController";
import { deliveryDetailsController } from "../Controllers/DeliveryDetailsController";
import { addressesController } from "../../../Common/Controllers/AddressesController";
import { alertController } from "../../../Common/Controllers/AlertController";
import LoadingOverlay from "../../../Common/Components/LoadingOverlay";
import { AddressDTO } from "../../../Common/DTOs/Address/AddressDTO";
import { AddressDialog } from "../../AccountPage/Components/AddressDialog";
import { userAccountController } from "../../../Common/Controllers/UserAccountController";
import { basketController } from "../../../Common/Controllers/BasketController";
import { stockiestPortalController } from "../../../Common/Controllers/StockiestPortalController";

export const DeliveryDetailsSubpage = observer(() => {
  const PREV_STEP = 0;
  const CURRENT_STEP = 1;
  const NEXT_STEP = 2;

  const navigate = useNavigate();
  orderController.activeStep = CURRENT_STEP;

  const [chooseAddressOpen, setChooseAddressOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<AddressDTO | null>(null);

  useEffect(() => {
    if (!basketController.isBasketDataLoading && basketController.basket?.purchasableItems?.length == 0) {
      navigate("/checkout/step-1")
    }
  }, [basketController.isBasketDataLoading]);

  useEffect(() => {
    if (deliveryDetailsController.deliveryOptions.length > 0) {
      const cheapest = deliveryDetailsController.deliveryOptions.reduce(
        (min, q) => (q.costToPublisher < min.costToPublisher ? q : min)
      );
      orderController.setSelectedQuote(cheapest);
    }
  }, [deliveryDetailsController.deliveryOptions]);

  const handleSelectAddress = (address: AddressDTO) => {
    void addressesController.selectDeliveryAddress(address);
    setChooseAddressOpen(false);
  };

  const handleDeleteAddress = async (id: number) => {
    await addressesController.deleteAddress(id);
    if (addressesController.selectedDeliveryAddress?.id === id) {
      addressesController.selectedDeliveryAddress = null;
    }
  };

  const handleEditAddress = (address: AddressDTO) => {
    setAddressToEdit(address);
    setChooseAddressOpen(false);
    setEditModalOpen(true);
  };

  const handleAddAddress = () => {
    setChooseAddressOpen(false);
    setAddModalOpen(true);
  };

  const navigateToReviewOrder = () => {
    orderController.activeStep = PREV_STEP;
    navigate("/checkout/step-1");
  };

  const navigateToPay = async () => {
    if (!addressesController.selectedDeliveryAddress) return;
    const result = await deliveryDetailsController.checkDeliveryDetailsCorrect();
    if (result?.isValid) {
      orderController.activeStep = NEXT_STEP;
      navigate("/checkout/step-3");
    } else {
      alertController.displayAlert(
        "Address validation failed",
        result?.errors.map((e) => e.errorMessage) || null,
        "error"
      );
    }
  };

  const selectedAddress = addressesController.selectedDeliveryAddress;

  useEffect(() => {
    if (!!userAccountController.stockist && addressesController.selectedDeliveryAddress?.id && !addressesController.isLoading) {
      void deliveryDetailsController.getDeliveryOptions();
    }
  }, [userAccountController.stockist, addressesController.selectedDeliveryAddress, addressesController.isLoading]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col h-full">
        <LoadingOverlay isVisible={userAccountController.isUserAccountDataLoading || deliveryDetailsController.validationInProgress} />

        <div className="flex flex-col lg:flex-row lg:items-center gap-4 text-white w-full h-0 flex-auto md:p-6 overflow-y-auto max-w-full md:max-w-[780px] lg:max-w-[900px] xl:max-w-[1100px] 2xl:max-w-[1200px] mx-auto">
          <div className="w-full lg:w-1/2 h-[380px] border-y md:border border-gray-700 text-white p-6 md:p-8 md:rounded-2xl">
            <LoadingOverlay isVisible={addressesController.isLoading} />

            <Typography variant="h4" color="white" className="pb-3 border-b border-light-grey/40 animate-fadeUp text-3xl">
              Destination
            </Typography>

            {(userAccountController.isUserAccountDataLoading || basketController.isBasketDataLoading || addressesController.isLoading) ?
              <div className="flex justify-center items-center py-8 text-gray-400 animate-fadeUp">
                <Spinner width={40} height={40} />
              </div> :
              selectedAddress ? (
                <Card className="bg-main text-white rounded-2xl py-4 shadow-md animate-fadeUp">
                  <CardBody className="p-0">
                    <Typography variant="h6" className="mb-3 pb-2 border-b border-light-grey/40 max-w-[200px]">
                      {selectedAddress.description}
                    </Typography>
                    <p>{selectedAddress.firstName} {selectedAddress.surname}</p>
                    {selectedAddress.companyName && <p>{selectedAddress.companyName}</p>}
                    <p>{selectedAddress.address1}</p>
                    {selectedAddress.address2 && <p>{selectedAddress.address2}</p>}
                    {selectedAddress.address3 && <p>{selectedAddress.address3}</p>}
                    {selectedAddress.address4 && <p>{selectedAddress.address4}</p>}
                    <p>{selectedAddress.postCode}</p>
                    <p>{selectedAddress.country}</p>
                  </CardBody>
                  <CardFooter className="p-0 mt-3">
                    <Button size="sm" color="white" className="text-black" onClick={() => setChooseAddressOpen(true)} disabled={deliveryDetailsController.isDeliveryOptionsLoading}>
                      Change
                    </Button>
                  </CardFooter>
                </Card>
              ) : (
                <div>
                  <Typography>No delivery address selected.</Typography>
                  <Button size="sm" color="white" className="text-black mt-6" onClick={() => setChooseAddressOpen(true)}>
                    Choose address
                  </Button>
                </div>
              )}
          </div>

          <div className="w-full lg:w-1/2 h-[380px] border-y md:border border-gray-700 text-white p-6 md:p-8 md:rounded-2xl">
            <Typography
              variant="h4"
              color="white"
              className="pb-3 border-b border-light-grey/40 animate-fadeUp text-3xl"
            >
              Delivery
            </Typography>

            {selectedAddress && (
              (userAccountController.isUserAccountDataLoading || basketController.isBasketDataLoading || addressesController.isLoading || deliveryDetailsController.isDeliveryOptionsLoading) ? (
                <div className="flex justify-center items-center py-8 text-gray-400 animate-fadeUp">
                  <Spinner width={40} height={40} />
                </div>
              ) : deliveryDetailsController.deliveryOptions.length === 0 ? (
                <div className="flex flex-col justify-center items-start py-4 text-gray-400 animate-fadeUp">
                  <Typography>No delivery options available.</Typography>
                  <Button
                    size="sm"
                    color="white"
                    className="text-black mt-6"
                    onClick={deliveryDetailsController.getDeliveryOptions}
                  >
                    Refresh
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col h-full pt-4 pb-6 md:pb-4">
                  <div className="flex gap-4 overflow-x-auto mb-1 px-1 pt-1 pb-4 animate-fadeUp h-full">
                    {
                      deliveryDetailsController.deliveryOptions
                        .slice()
                        .sort((a, b) => a.costToPublisher - b.costToPublisher)
                        .map((q) => {
                          const isSelected = orderController.selectedQuote?.serviceType === q.serviceType && orderController.selectedQuote?.costToPublisher === q.costToPublisher;

                          return (
                            <Card
                              key={`${q.serviceType}-${q.costToPublisher}`}
                              className={`flex-shrink-0 w-64 p-4 rounded-xl outline outline-1 transition duration-300 cursor-pointer ${isSelected
                                ? "outline-2 outline-white bg-gray-700"
                                : "outline-light-grey bg-dark-grey hover:bg-gray-800"
                                }`}
                              onClick={() => orderController.setSelectedQuote(q)}
                            >
                              <CardBody className="p-0 h-full">
                                <div className="flex justify-between items-center mb-2 border-b border-light-grey/40 pb-2">
                                  <div className="flex flex-col gap-1">
                                    <Typography variant="h6" className="font-bold text-white">
                                      {q.serviceName}
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      color="gray"
                                      className="text-sm font-normal text-gray-400"
                                    >
                                      {q.serviceDescriptionDisplay}
                                    </Typography>
                                  </div>
                                  <img
                                    src={`/images/couriers/${Couriers[q.courier]}.webp`}
                                    alt={q.courier.toString()}
                                    className="w-16 h-10 object-contain"
                                  />
                                </div>
                                <p className="text-sm text-gray-400">
                                  <span className="font-bold">Est. delivery date: </span>
                                  {q.displayDeliveryDate}
                                </p>
                                <p className="font-bold mt-2 text-lg text-white">
                                  {q.costToPublisher.toFixed(2)} {q.currency}
                                </p>
                              </CardBody>
                            </Card>
                          );
                        })
                    }
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        <div className="bg-gray-800 border-y-2 overflow-hidden sm:py-2 xlh:py-4 grid grid-cols-1 grid-rows-2 items-center sm:gap-2 sm:grid-cols-3 sm:grid-rows-1 sm:text-center">
          <div className="hidden sm:flex justify-start mb-2 sm:mb-0">
            <Button
              size="md"
              className="rounded-none h-auto w-full sm:w-auto !px-3 !py-2 lgh:!px-6 lgh:!py-3 hover:bg-white/90"
              color="white"
              onClick={navigateToReviewOrder}
              disabled={deliveryDetailsController.isDeliveryOptionsLoading}
            >
              <div className="flex items-center justify-center">
                <ArrowLeftIcon color="black" className="h-4 w-4" />
                <span className="ml-2 text-sm lgh:text-xl">Review Order</span>
              </div>
            </Button>
          </div>
          <div className="flex justify-center items-center text-white">
            {basketController.isBasketDataLoading ? (
              <Spinner />
            ) : (
              <Typography
                variant="h4"
                color="white"
                className="text-base lgh:text-3xl text-center"
              >
                Your total is: £
                {stockiestPortalController.totalPriceInclDiscountInPoundsWithDelivery?.toFixed(2)}
              </Typography>
            )}
          </div>

          <div className="flex justify-end gap-2 sm:justify-end">
            <div className="flex sm:hidden w-full justify-between">
              <Button
                size="md"
                className="rounded-none h-auto w-full !px-3 !py-2 lgh:!px-6 lgh:!py-3 hover:bg-white/90"
                color="white"
                onClick={navigateToReviewOrder}
                disabled={deliveryDetailsController.isDeliveryOptionsLoading}
              >
                <div className="flex items-center justify-center">
                  <ArrowLeftIcon color="black" className="h-4 w-4" />
                  <span className="ml-2 text-sm lgh:text-xl">Review Order</span>
                </div>
              </Button>

              <Button
                size="md"
                className="rounded-none h-auto w-full !px-3 !py-2 lgh:!px-6 lgh:!py-3 hover:bg-white/90"
                color="white"
                onClick={navigateToPay}
                disabled={
                  !addressesController.selectedDeliveryAddress ||
                  !orderController.selectedQuote ||
                  deliveryDetailsController.isDeliveryOptionsLoading
                }
              >
                <div className="flex items-center justify-center">
                  <span className="mr-2 text-sm lgh:text-xl">Payment</span>
                  <ArrowRightIcon color="black" className="h-4 w-4" />
                </div>
              </Button>
            </div>

            <div className="hidden sm:flex w-full justify-end gap-2">
              <Button
                size="md"
                className="rounded-none h-auto w-auto !px-3 !py-2 lgh:!px-6 lgh:!py-3 hover:bg-white/90"
                color="white"
                onClick={navigateToPay}
                disabled={
                  !addressesController.selectedDeliveryAddress ||
                  !orderController.selectedQuote ||
                  deliveryDetailsController.isDeliveryOptionsLoading
                }
              >
                <div className="flex items-center justify-center">
                  <span className="mr-2 text-sm lgh:text-xl">Payment</span>
                  <ArrowRightIcon color="black" className="h-4 w-4" />
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={chooseAddressOpen} handler={setChooseAddressOpen} size="lg" className="bg-main/90 text-light-grey border border-light-grey rounded-none md:!rounded-xl shadow-2xl p-2 md:p-6 !max-h-[900px] m-0 md:m-4 !w-full !h-full max-w-full md:!max-w-[680px] lg:!max-w-[800px] xl:!max-w-[1000px] xl:!max-w-[1280px] lg:h-[80vh] !overflow-y-hidden transition-all">
        <div className="h-[10%] min-h-[60px] border-b border-light-grey/40 flex flex-col items-center justify-center text-center pt-4 pb-6">
          <Typography variant="h4" color="white" className="text-lg md:text-4xl font-bold">Choose delivery address</Typography>
        </div>

        <div className="h-[75%] md:h-[80%] overflow-y-auto p-4 md:p-12 flex flex-col gap-4">
          {addressesController.allAddresses.map(addr => (
            <Card
              key={addr.id}
              className={`text-white shadow-lg rounded-xl border border-light-grey transition-all ${addressesController.selectedDeliveryAddress?.id === addr.id ? "bg-gray-700 border-2 border-white" : "bg-dark-grey hover:bg-gray-800 cursor-pointer"}`}
              onClick={() => handleSelectAddress(addr)}
            >
              <CardBody>
                <Typography variant="h6"
                  className="mb-5 pb-2 border-b border-light-grey/30 max-w-[200px]">{addr.description}</Typography>
                <p>{addr.firstName} {addr.surname}</p>
                {addr.companyName && <p>{addr.companyName}</p>}
                <p>{addr.address1}</p>
                {addr.address2 && <p>{addr.address2}</p>}
                <p>{addr.postCode}, {addr.address3}, {addr.country}</p>
                <p>{addr.address4}</p>
                <p>{addr.phoneDay}</p>
              </CardBody>
              <CardFooter className="flex gap-2">
                <Button
                  size="sm"
                  color="white"
                  className="text-black"
                  onClick={(e) => { e.stopPropagation(); handleEditAddress(addr); }}
                  disabled={deliveryDetailsController.isDeliveryOptionsLoading}
                >Edit</Button>
                <Button
                  size="sm"
                  color="red"
                  onClick={(e) => { e.stopPropagation(); void handleDeleteAddress(addr.id); }}
                  disabled={deliveryDetailsController.isDeliveryOptionsLoading}
                >Delete</Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="md:pt-4 overflow-y-hidden h-[15%] md:h-[10%] max-h-[72px] md:max-h-[89px] border-t border-light-grey/40 flex justify-center md:justify-end items-center gap-3">
          <Button size="lg" variant="text" color="red" onClick={() => setChooseAddressOpen(false)}>Close</Button>
          <Button
            color="green"
            className="bg-purple hover:bg-purple/80 hover:shadow-none"
            onClick={handleAddAddress}
            disabled={deliveryDetailsController.isDeliveryOptionsLoading}
          >
            Add new address
          </Button>
        </div>
      </Dialog>

      <AddressDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        initialData={addressToEdit ?? undefined}
        onSubmit={async (data) => {
          await addressesController.editAddress(data);
          setEditModalOpen(false);
          setChooseAddressOpen(true);

          if (addressesController.selectedDeliveryAddress?.id === data.id) {
            handleSelectAddress(data);
            void deliveryDetailsController.getDeliveryOptions();
          }
        }}
      />

      <AddressDialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSubmit={async (data) => {
          await addressesController.addAddress(data);
          setAddModalOpen(false);
          setChooseAddressOpen(true);
        }}
      />
    </div>
  );
});
